/**
 * @file Renders the modal that hosts the distribution schedule form.
 */
import { useState } from "react";
import dayjs from "dayjs";
import { useParams, useOutletContext } from "react-router-dom";

import { Modal } from "@components/Page";
import { MODAL_LARGE_WIDTH, MODAL_LARGE_HEIGHT } from "@components/Constants";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";
import { ACTIVE as SCHEDULE_STATUS_ACTIVE } from "@api/services/transactions/shapes/ScheduleStatusEnum";
import { INVESTMENT_RETURN as INVESTMENT_RETURN_TRANSACTION_TYPE } from "@api/services/transactions/shapes/TransactionTypeEnum";
import { MONTHLY as DISTRIBUTION_FREQUENCY_MONTHLY } from "@api/services/transactions/shapes/DistributionFrequencyEnum";

import DistributionsScheduleForm, { getNextRecurrenceDate } from "./DistributionsScheduleForm";
import type { Investment, OutletContext, ScheduleFormValues, Schedule } from "./types";

const LABEL_TITLE = "Distributions Schedule";

interface ModalHandle {
  current: {
    open: () => void;
    close: () => void;
  };
}

type Props = {
  modal: ModalHandle;
  schedule?: Schedule;
  investments: Investment[];
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  deleteSchedule: (scheduleId: string) => Promise<unknown>;
  enableSchedule: (scheduleId: string) => Promise<unknown>;
  disableSchedule: (scheduleId: string) => Promise<unknown>;
}


/**
 * Renders the distribution schedule modal, exposing its open and close controls through `modal`.
 */
const DistributionsScheduleModal = ({
  schedule = undefined,
  modal,
  investments,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  enableSchedule,
  disableSchedule,
}: Props) => {
  const [ isOpen, setIsOpen ] = useState(false);

  /**
   * Closes the modal.
   */
  const onClose = () =>
    setIsOpen(false);

  /**
   * Opens the modal.
   */
  modal.current.open = () =>
    setIsOpen(true);

  /**
   * Closes the modal.
   */
  modal.current.close = () =>
    onClose();

  const { id: projectId } = useParams();
  const { project } = useOutletContext<OutletContext>();
  const { name } = project;

  const initialValues: ScheduleFormValues = schedule
    ? {
        name: schedule.name,
        startDate: schedule.startDate,
        nextDate: schedule.nextDate,
        shouldRoundCents: schedule.shouldRoundCents,
        distributionFrequency: schedule.distributionFrequency,
        status: schedule.status,
        transactionType: INVESTMENT_RETURN_TRANSACTION_TYPE,
        projectId: schedule.projectId,
        projectName: schedule.projectName,
        distributions: schedule.distributions,
      }
    : {
        name,
        nextDate: getNextRecurrenceDate(dayjs().toISOString(), DISTRIBUTION_FREQUENCY_MONTHLY),
        startDate: dayjs().toISOString(),
        shouldRoundCents: false,
        distributionFrequency: DISTRIBUTION_FREQUENCY_MONTHLY,
        status: SCHEDULE_STATUS_ACTIVE,
        transactionType: INVESTMENT_RETURN_TRANSACTION_TYPE,
        projectId,
        projectName: name,
      };

  const modalProps = {
    title: LABEL_TITLE,
    width: MODAL_LARGE_WIDTH,
    destroyOnClose: true,
    isOpen,
    onClose,
  };

  const formProps = {
    scheduleId: schedule?.id,
    onClose,
    investments,
    initialValues,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    enableSchedule,
    disableSchedule,
  };

  return (
    <Modal key={projectId} {...modalProps}>
      <div style={{ minHeight: MODAL_LARGE_HEIGHT }}>
        <DistributionsScheduleForm {...formProps} />
      </div>
    </Modal>
  );
};

export default DistributionsScheduleModal;
